import React from 'react';

import { DefaultContext } from '@apollo/client';

import { FileResponseInfoFragment, useUploadFileMutation } from 'src/api';

type FileUploadHookParams = {
  context?: DefaultContext;
  skip?: boolean;
};

type FileUploadHookResponse = {
  uploadFile: (
    file: File,
    isPublic?: boolean,
    context?: DefaultContext,
  ) => Promise<FileResponseInfoFragment>;
  uploadFiles: (
    filesList: Array<{ file: File; isPublic?: boolean }>,
    context?: DefaultContext,
  ) => Promise<FileResponseInfoFragment[]>;
  uploading: boolean;
};

export const useFileUpload = (params: FileUploadHookParams = {}): FileUploadHookResponse => {
  const [uploading, setUploading] = React.useState(false);

  const [uploadFileMutation] = useUploadFileMutation(params);

  const internalUploadFile: FileUploadHookResponse['uploadFile'] = React.useCallback(
    async (file, isPublic = true, context) => {
      const { data } = await uploadFileMutation({
        variables: {
          file,
          public: isPublic,
        },
        context,
      });

      if (!data) {
        throw new Error(`Error while uploading "${file.name}" file`);
      }

      return data.uploadFile;
    },
    [uploadFileMutation],
  );

  const uploadFile: FileUploadHookResponse['uploadFile'] = React.useCallback(
    async (file, isPublic = true, context) => {
      setUploading(true);
      try {
        return await internalUploadFile(file, isPublic, context);
      } finally {
        setUploading(false);
      }
    },
    [internalUploadFile],
  );

  const uploadFiles: FileUploadHookResponse['uploadFiles'] = React.useCallback(
    async (filesList: Array<{ file: File; isPublic?: boolean }>, context?: DefaultContext) => {
      setUploading(true);
      try {
        // Potentially if multipart will start supporting batching
        // you will reach multipart data limit, so we don't use Promise.all here
        const files: FileResponseInfoFragment[] = [];
        for (const fileItem of filesList) {
          const uploadResult = await internalUploadFile(fileItem.file, fileItem.isPublic, context);
          files.push(uploadResult);
        }
        return files;
      } finally {
        setUploading(false);
      }
    },
    [internalUploadFile],
  );

  return { uploadFile, uploadFiles, uploading };
};
